import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { ChipInput } from '../components/ChipInput'
import { WorkingHoursDial } from '../components/config/WorkingHoursDial'
import { TimezoneSelect } from '../components/config/TimezoneSelect'
import { updateChannel } from '../api/client'
import type { ChannelConfig, ChannelConfigUpdate, RejectedSlug } from '../types'
import styles from './Onboarding.module.css'

// ── Types ─────────────────────────────────────────────────────────────────

type JourneyType = 'team' | 'personal'

type TeamStep = 'channel' | 'journey' | 'settings' | 'features' | 'live'
type PersonalStep = 'channel' | 'journey' | 'settings' | 'features' | 'live'
type Step = TeamStep | PersonalStep

const TEAM_STEPS:     Step[] = ['channel', 'journey', 'settings', 'features', 'live']
const PERSONAL_STEPS: Step[] = ['channel', 'journey', 'settings', 'features', 'live']

const STEP_LABELS: Record<Step, string> = {
  channel:  'Channel',
  journey:  'Setup type',
  settings: 'Configuration',
  features: 'Features',
  live:     'Go live',
}

const TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/New_York',    label: 'Boston / New York (ET)' },
  { value: 'America/Chicago',     label: 'Chicago (CT)' },
  { value: 'America/Denver',      label: 'Denver (MT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'America/Sao_Paulo',   label: 'São Paulo (BRT)' },
  { value: 'Europe/Dublin',       label: 'Ireland / Dublin (IST)' },
  { value: 'Europe/London',       label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',        label: 'Paris / Central Europe (CET)' },
  { value: 'Asia/Kolkata',        label: 'India (IST)' },
  { value: 'Asia/Singapore',      label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo',          label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney',    label: 'Sydney (AEST)' },
  { value: 'UTC',                 label: 'UTC' },
]

// ── Feature card definitions ───────────────────────────────────────────────

interface FeatureDef {
  key: string
  icon: string
  title: string
  desc: string
  color: string
  bg: string
}

const TEAM_FEATURES: FeatureDef[] = [
  { key: 'reviewUpdates',     icon: 'notifications',     title: 'Review Updates',       desc: 'Post when a PR is opened, reviewed, approved, or merged',                              color: '#00897b', bg: '#e0f2f1' },
  { key: 'nudgeEnabled',      icon: 'schedule',          title: 'Nudge Reminders',      desc: 'Remind the channel when a PR has been waiting too long for review',                   color: '#e37400', bg: '#fef7e0' },
  { key: 'escalationEnabled', icon: 'escalator_warning', title: 'Manager Escalation',   desc: 'Notify a manager after repeated nudges with no response',                             color: '#c5221f', bg: '#fce8e6' },
  { key: 'weeklyDigest',      icon: 'bar_chart',         title: 'Weekly Channel Digest', desc: 'Monday morning summary of open PRs and review activity',                             color: '#7b57d6', bg: '#ede7f6' },
  { key: 'personalDigest',    icon: 'person',            title: 'Personal PR Digest',   desc: 'DM each engineer their personal PR stats every Monday',                               color: '#1a73e8', bg: '#e8f0fe' },
  { key: 'reviewerTagging',   icon: 'alternate_email',   title: 'Tag Reviewers',        desc: 'Mention requested reviewers who are members of this channel on new PR notifications', color: '#00695c', bg: '#e0f2f1' },
]

const PERSONAL_FEATURES: FeatureDef[] = [
  { key: 'reviewUpdates',  icon: 'notifications',    title: 'Review Updates',     desc: 'Get notified on your PRs — approvals, comments, and merges',  color: '#00897b', bg: '#e0f2f1' },
  { key: 'personalDigest', icon: 'person',           title: 'Personal PR Digest', desc: 'DM with your personal PR stats every Monday morning',          color: '#1a73e8', bg: '#e8f0fe' },
]

// ── Props ──────────────────────────────────────────────────────────────────

interface Props {
  channels: ChannelConfig[]
  sessionToken?: string
  preselectedChannelId?: string
}

// ── Component ─────────────────────────────────────────────────────────────

export function Onboarding({ channels, sessionToken, preselectedChannelId }: Props) {
  const navigate = useNavigate()

  // Step state
  const [step, setStep] = useState<Step>(preselectedChannelId ? 'journey' : 'channel')
  const [journey, setJourney] = useState<JourneyType | null>(null)

  // Channel
  const preselected = channels.find(c => c.channelId === preselectedChannelId) ?? null
  const [selectedChannel, setSelectedChannel] = useState<ChannelConfig | null>(preselected)
  const [channelSearch, setChannelSearch] = useState('')

  // Team settings
  const [authorTeamSlugs, setAuthorTeamSlugs] = useState<string[]>([])
  const [authorTeamInput, setAuthorTeamInput] = useState('')
  const [timezone, setTimezone] = useState('UTC')
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00')
  const [workingHoursEnd, setWorkingHoursEnd] = useState('17:00')

  // Personal settings
  const [watchedAuthorLogins, setWatchedAuthorLogins] = useState<string[]>([])

  // Features (keyed by FeatureDef.key)
  const teamDefaults = new Set(['reviewUpdates', 'nudgeEnabled', 'escalationEnabled', 'weeklyDigest'])
  const personalDefaults = new Set(['reviewUpdates', 'personalDigest'])
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(new Set())

  // Slug validation
  const [rejected, setRejected] = useState<RejectedSlug[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const steps = journey === 'personal' ? PERSONAL_STEPS : TEAM_STEPS
  const stepIndex = steps.indexOf(step)

  const filtered = channelSearch
    ? channels.filter(c => c.channelName.toLowerCase().includes(channelSearch.toLowerCase()))
    : channels

  function pickJourney(j: JourneyType) {
    setJourney(j)
    setEnabledFeatures(new Set(j === 'team' ? teamDefaults : personalDefaults))
    setStep('settings')
  }

  function toggleFeature(key: string) {
    setEnabledFeatures(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const effectiveChannelId = selectedChannel?.channelId ?? preselectedChannelId
  const effectiveChannelName = selectedChannel?.channelName ?? preselectedChannelId ?? ''

  async function finish() {
    if (!effectiveChannelId || !sessionToken) return
    setSaving(true)
    setSaveError(null)
    setRejected([])

    const on = enabledFeatures

    const update: ChannelConfigUpdate = {
      timezone,
      workingHoursStart,
      workingHoursEnd,
      enabledNotifications: undefined,      // omit = backend default (all on except REVIEW_COMMENT)
      reactionNotifications: ['DEFAULTS'], // reset to all-on
      replyNotifications: ['DEFAULTS'],    // reset to all-on
      nudgeEnabled:          journey === 'team' ? on.has('nudgeEnabled') : false,
      nudgeThresholdDays:    2,
      escalationEnabled:     journey === 'team' ? on.has('escalationEnabled') : false,
      escalationAfterNudges: 3,
      weeklyDigestEnabled:   journey === 'team' ? on.has('weeklyDigest') : false,
      personalDigestEnabled: on.has('personalDigest'),
      reviewerTaggingEnabled: journey === 'team' ? on.has('reviewerTagging') : false,
      dmReviewersEnabled:    on.has('dmReviewers'),
      scopedUserGroupTagging: journey === 'team',
      openNotificationTemplate: 'STANDARD',
      reviewReplyTemplate: 'STANDARD',
    }

    if (journey === 'team') {
      const teams = authorTeamSlugs.length > 0 ? authorTeamSlugs : authorTeamInput.trim() ? [authorTeamInput.trim()] : []
      update.githubTeamSlugs = teams
      update.authorTeamSlugs = teams
    } else {
      update.watchedAuthorLogins = watchedAuthorLogins
      update.watchedReviewerLogins = watchedAuthorLogins
    }

    try {
      const { rejected: rej } = await updateChannel(sessionToken, effectiveChannelId, update)
      if (rej.length > 0) {
        setRejected(rej)
        setSaveError(`${rej.length} team slug${rej.length > 1 ? 's' : ''} were invalid and not saved.`)
        setSaving(false)
        return
      }
      setStep('live')
    } catch (err: unknown) {
      const e = err as { message?: string }
      setSaveError(e?.message ?? 'Something went wrong. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate('/')}>
        <Icon name="arrow_back" size="sm" /> Back to channels
      </button>

      {/* Stepper — only shown after journey selected */}
      {journey && step !== 'live' && (
        <div className={styles.steps}>
          {steps.filter(s => s !== 'live').map((s, i) => (
            <Fragment key={s}>
              {i > 0 && <div className={`${styles.line} ${stepIndex > i ? styles.lineDone : ''}`} />}
              <div className={`${styles.step} ${step === s ? styles.stepActive : ''} ${stepIndex > i ? styles.stepDone : ''}`}>
                <div className={styles.circle}>
                  {stepIndex > i ? <Icon name="check" size={16} /> : i + 1}
                </div>
                <span className={styles.stepLabel}>{STEP_LABELS[s]}</span>
              </div>
            </Fragment>
          ))}
        </div>
      )}

      {/* ── Step: Channel ──────────────────────────────────────────── */}
      {step === 'channel' && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Select a channel to set up</div>
            <div className={styles.cardSub}>Only channels you're a member of are shown.</div>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.searchWrap}>
              <Icon name="search" size="sm" className={styles.searchIcon} />
              <input
                type="search"
                placeholder="Search channels"
                value={channelSearch}
                onChange={e => setChannelSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <div className={styles.channelList}>
              {filtered.map(ch => (
                <div
                  key={ch.channelId}
                  className={`${styles.channelOption} ${selectedChannel?.channelId === ch.channelId ? styles.channelSelected : ''}`}
                  onClick={() => setSelectedChannel(ch)}
                >
                  <Icon name="tag" size="md" className={styles.coIcon} />
                  <div className={styles.coName}>{ch.channelName}</div>
                  <div className={styles.coCheck}>
                    {selectedChannel?.channelId === ch.channelId && <Icon name="check" size={13} />}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.actions}>
              <button className={styles.btnPrimary} disabled={!selectedChannel} onClick={() => setStep('journey')}>
                Continue <Icon name="arrow_forward" size="sm" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step: Journey type ─────────────────────────────────────── */}
      {step === 'journey' && (
        <>
          {effectiveChannelId && <ChannelBar channelName={effectiveChannelName} />}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>What are you setting this up for?</div>
              <div className={styles.cardSub}>Choose the setup that best describes how this channel will be used.</div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.journeyGrid}>
                <div className={styles.journeyOption} onClick={() => pickJourney('team')}>
                  <div className={styles.journeyIcon} style={{ background: '#e0f2f1', color: '#00897b' }}>
                    <Icon name="groups" size="md" />
                  </div>
                  <div className={styles.journeyTitle}>Team review channel</div>
                  <div className={styles.journeyDesc}>Route team PRs to a shared channel with full review lifecycle management</div>
                  <div className={styles.journeyArrow}><Icon name="arrow_forward" size="sm" /></div>
                </div>
                <div className={styles.journeyOption} onClick={() => pickJourney('personal')}>
                  <div className={styles.journeyIcon} style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                    <Icon name="person" size="md" />
                  </div>
                  <div className={styles.journeyTitle}>Personal PR inbox</div>
                  <div className={styles.journeyDesc}>Track your own PRs and get direct updates — ideal for your own Slack channel or DM</div>
                  <div className={styles.journeyArrow}><Icon name="arrow_forward" size="sm" /></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Step: Settings ─────────────────────────────────────────── */}
      {step === 'settings' && (
        <>
          {effectiveChannelId && <ChannelBar channelName={effectiveChannelName} journey={journey ?? undefined} />}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                {journey === 'team' ? 'Configure your team channel' : 'Configure your personal inbox'}
              </div>
              <div className={styles.cardSub}>
                {journey === 'team'
                  ? 'Set up which teams to watch and your working schedule.'
                  : 'Set up which GitHub users to watch and your working schedule.'}
              </div>
            </div>
            <div className={styles.cardBody}>
              {journey === 'team' ? (
                <>
                  <ChipInput
                    label="Author teams"
                    values={authorTeamSlugs}
                    onChange={setAuthorTeamSlugs}
                    onInputChange={setAuthorTeamInput}
                    placeholder="e.g. enterprise-away — must match your GitHub team name exactly"
                    hint="Subscribe to PRs opened by members of these GitHub teams — useful for staying in the loop with your own team's work, across internal and external repositories regardless of who reviews it"
                  />
                  {rejected.filter(r => r.kind === 'review' || r.kind === 'author').map(r => (
                    <div key={r.slug} className={styles.rejectedRow}>
                      <Icon name="error_outline" size="sm" style={{ color: 'var(--red)', flexShrink: 0 }} />
                      <code>{r.slug}</code>
                      <span>{r.reason === 'not_found' ? 'Team not found on GitHub' : r.reason === 'org_wide' ? 'Org-wide teams cannot be used' : 'GitHub unavailable — please retry'}</span>
                    </div>
                  ))}
                </>
              ) : (
                <ChipInput
                  label="Watched users"
                  values={watchedAuthorLogins}
                  onChange={setWatchedAuthorLogins}
                  placeholder="e.g. your-github-handle"
                  hint="Subscribe to PR events from these specific GitHub users — by authorship and review assignment"
                />
              )}

              <div className={styles.field}>
                <label className={styles.label}>Timezone</label>
                <TimezoneSelect
                  value={timezone}
                  options={TIMEZONES}
                  onChange={setTimezone}
                />
                <div className={styles.hint}>Used for nudge scheduling and digest delivery</div>
              </div>

              <div className={`${styles.field} ${styles.fieldLast}`}>
                <label className={styles.label}>Working hours</label>
                <WorkingHoursDial
                  start={workingHoursStart}
                  end={workingHoursEnd}
                  onChange={(s, e) => { setWorkingHoursStart(s); setWorkingHoursEnd(e) }}
                />
                <div className={styles.hint}>Nudges are only sent during these hours</div>
              </div>

              <div className={styles.twoActions}>
                <button className={styles.btnOutline} onClick={() => setStep('journey')}>
                  <Icon name="arrow_back" size="sm" /> Back
                </button>
                <button
                  className={styles.btnPrimary}
                  disabled={journey === 'team' && authorTeamSlugs.length === 0 && authorTeamInput.trim().length === 0}
                  onClick={() => setStep('features')}
                >
                  Continue <Icon name="arrow_forward" size="sm" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Step: Features ─────────────────────────────────────────── */}
      {step === 'features' && (
        <>
          {effectiveChannelId && <ChannelBar channelName={effectiveChannelName} journey={journey ?? undefined} />}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>Enable features</div>
              <div className={styles.cardSub}>Pre-selected based on your setup type. You can change these any time from settings.</div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.featureGrid}>
                {(journey === 'team' ? TEAM_FEATURES : PERSONAL_FEATURES).map(f => {
                  const on = enabledFeatures.has(f.key)
                  return (
                    <div
                      key={f.key}
                      className={`${styles.featureCard} ${on ? styles.featureSelected : ''}`}
                      onClick={() => toggleFeature(f.key)}
                    >
                      <div className={styles.fcCheck} style={on ? { background: 'var(--blue)', borderColor: 'var(--blue)', color: 'white' } : undefined}>
                        {on && <Icon name="check" size={12} />}
                      </div>
                      <div className={styles.fcIcon} style={on ? { background: 'white', color: 'var(--blue)' } : { background: f.bg, color: f.color }}>
                        <Icon name={f.icon} size="md" />
                      </div>
                      <div className={styles.fcTitle}>{f.title}</div>
                      <div className={styles.fcDesc}>{f.desc}</div>
                    </div>
                  )
                })}
              </div>

              {saveError && (
                <div className={styles.saveError}>
                  <Icon name="error_outline" size="sm" /> {saveError}
                </div>
              )}

              <div className={styles.twoActions}>
                <button className={styles.btnOutline} onClick={() => setStep('settings')}>
                  <Icon name="arrow_back" size="sm" /> Back
                </button>
                <button className={styles.btnPrimary} onClick={finish} disabled={saving}>
                  {saving
                    ? <><Icon name="hourglass_empty" size="sm" /> Setting up…</>
                    : <>Finish setup <Icon name="check" size="sm" /></>
                  }
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Step: Live ─────────────────────────────────────────────── */}
      {step === 'live' && effectiveChannelId && (
        <LiveScreen
          channelName={effectiveChannelName}
          journey={journey ?? 'team'}
          enabledFeatures={enabledFeatures}
          onExplore={() => navigate(`/channels/${effectiveChannelId}`)}
        />
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ChannelBar({ channelName, journey }: { channelName: string; journey?: JourneyType }) {
  return (
    <div className={styles.summaryBar}>
      <Icon name="tag" size="sm" style={{ color: 'var(--muted)' }} />
      <div style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{channelName}</div>
      {journey && (
        <span className={styles.journeyBadge}>
          <Icon name={journey === 'team' ? 'groups' : 'person'} size="sm" />
          {journey === 'team' ? 'Team channel' : 'Personal inbox'}
        </span>
      )}
    </div>
  )
}

function LiveScreen({ channelName, journey, enabledFeatures, onExplore }: {
  channelName: string
  journey: JourneyType
  enabledFeatures: Set<string>
  onExplore: () => void
}) {
  const featureDefs = journey === 'team' ? TEAM_FEATURES : PERSONAL_FEATURES
  const active = featureDefs.filter(f => enabledFeatures.has(f.key))

  return (
    <div className={styles.liveWrap}>
      <div className={styles.liveCard}>
        <div className={styles.liveIconWrap}>
          <div className={styles.liveIconRing} />
          <div className={styles.liveIcon}>
            <Icon name="check" size={36} />
          </div>
        </div>

        <div className={styles.liveTitle}>Review Ops is live!</div>
        <div className={styles.liveSub}>
          <strong>#{channelName}</strong> is now set up and ready to go.
        </div>

        <div className={styles.liveFeatures}>
          <div className={styles.liveFeaturesLabel}>What's enabled</div>
          <div className={styles.liveFeaturesGrid}>
            {active.map(f => (
              <div key={f.key} className={styles.liveFeatureItem}>
                <div className={styles.liveFeatureIcon} style={{ background: f.bg, color: f.color }}>
                  <Icon name={f.icon} size="sm" />
                </div>
                <span>{f.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.liveActions}>
          <button className={styles.btnPrimary} onClick={onExplore}>
            <Icon name="tune" size="sm" /> Explore & personalise settings
          </button>
        </div>
      </div>
    </div>
  )
}
